/**
 * Calendar Assignment Component
 * Assigns team members' Google Calendars to specific entities
 * Integrated into GoogleCalendarAuth page
 */

import React, { useState, useEffect } from 'react';
import { orchestratorApi } from '../api/orchestrator';
import './GoogleCalendarAuth.css';

interface GoogleCalendar {
  id: string;
  summary: string;
  description: string | null;
  primary: boolean;
  isPrimary: boolean;
  backgroundColor: string;
  foregroundColor: string;
}

interface ConnectedUser {
  user_email: string;
  google_email: string;
  google_picture: string | null;
  entity_id: string | null;
  calendars: GoogleCalendar[];
  error?: string;
  last_error?: string | null;
  is_valid?: boolean;
}

interface EntityAssignment {
  entity_id: string;
  entity_name: string;
  entity_phone: string;
  assigned_user: string | null;
  assigned_calendars: string[];
  guest_emails: string[];
}

interface CalendarAssignmentProps {
  initialEntityId?: string | null;
}

export const CalendarAssignment: React.FC<CalendarAssignmentProps> = ({ initialEntityId }) => {
  const [assignments, setAssignments] = useState<EntityAssignment[]>([]);
  const [connectedUsers, setConnectedUsers] = useState<ConnectedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null);

  // Form state
  const [selectedEntityId, setSelectedEntityId] = useState<string>('');
  const [selectedUserEmail, setSelectedUserEmail] = useState<string>('');
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<string[]>([]);
  const [guestEmails, setGuestEmails] = useState<string[]>([]);
  const [guestEmailInput, setGuestEmailInput] = useState<string>('');
  const [savingGuestEmails, setSavingGuestEmails] = useState(false);

  useEffect(() => {
    loadAssignments();
  }, []);

  // Sync with parent entity selection
  useEffect(() => {
    if (initialEntityId && assignments.length > 0) {
      // Find the matching entity in assignments (match by id or phone)
      const match = assignments.find(
        a => a.entity_id === initialEntityId || a.entity_phone === initialEntityId
      );
      if (match) {
        setSelectedEntityId(match.entity_id);
      }
    }
  }, [initialEntityId, assignments]);

  // When entity is selected, load its current assignment
  useEffect(() => {
    if (selectedEntityId) {
      const assignment = assignments.find(a => a.entity_id === selectedEntityId);
      if (assignment && assignment.assigned_user) {
        setSelectedUserEmail(assignment.assigned_user);
        // Load calendars for this user (skip the first item which is user email)
        const userCalendars = assignment.assigned_calendars.slice(1);
        setSelectedCalendarIds(userCalendars);
      } else {
        setSelectedUserEmail('');
        setSelectedCalendarIds([]);
      }
      // Load guest emails
      if (assignment) {
        setGuestEmails(assignment.guest_emails || []);
      } else {
        setGuestEmails([]);
      }
    }
  }, [selectedEntityId, assignments]);

  // When user is selected, auto-select calendar if only one available
  useEffect(() => {
    if (selectedUserEmail) {
      const user = connectedUsers.find(u => u.user_email === selectedUserEmail);
      if (user && user.calendars.length === 1) {
        // Auto-select the only calendar
        setSelectedCalendarIds([user.calendars[0].id]);
      } else {
        // Check if this entity has a previous assignment for this user
        const assignment = assignments.find(a => a.entity_id === selectedEntityId);
        if (assignment && assignment.assigned_user === selectedUserEmail) {
          // Keep existing selection (skip first item which is user email)
          const userCalendars = assignment.assigned_calendars.slice(1);
          setSelectedCalendarIds(userCalendars);
        } else {
          // New assignment, clear selection
          setSelectedCalendarIds([]);
        }
      }
    }
  }, [selectedUserEmail, connectedUsers]);

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/calendar-assignments', {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok) {
        setAssignments(data.assignments);
        setConnectedUsers(data.connected_users);

        // Auto-select entity: use parent selection if provided, otherwise first entity
        if (data.assignments.length > 0 && !selectedEntityId) {
          if (initialEntityId) {
            const match = data.assignments.find((a: EntityAssignment) =>
              a.entity_id === initialEntityId || a.entity_phone === initialEntityId
            );
            if (match) {
              setSelectedEntityId(match.entity_id);
            } else {
              setSelectedEntityId(data.assignments[0].entity_id);
            }
          } else {
            setSelectedEntityId(data.assignments[0].entity_id);
          }
        }
      } else {
        showMessage('error', data.error || 'Failed to load calendar assignments');
      }
    } catch (error) {
      console.error('Failed to load calendar assignments:', error);
      showMessage('error', 'Failed to load calendar assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAssignment = async () => {
    if (!selectedEntityId || !selectedUserEmail) {
      showMessage('error', 'Please select both an entity and a user');
      return;
    }

    const user = connectedUsers.find(u => u.user_email === selectedUserEmail);

    // Check if user has calendars available
    if (!user || user.error) {
      showMessage('error', `Cannot assign ${selectedUserEmail}'s calendar: ${user?.error || 'User not found'}. Please ensure this user has connected their Google Calendar.`);
      return;
    }

    if (user.calendars.length === 0) {
      showMessage('error', 'This user has no calendars available. Please ensure they have connected their Google Calendar.');
      return;
    }

    if (selectedCalendarIds.length === 0) {
      showMessage('error', 'Please select at least one calendar');
      return;
    }

    setSaving(selectedEntityId);
    try {
      const response = await fetch(`/api/admin/calendar-assignments/${selectedEntityId}`, {
        method: 'POST',
        headers: {
          ...(await orchestratorApi.getAuthHeaders()),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_email: selectedUserEmail,
          calendar_ids: selectedCalendarIds
        })
      });

      const data = await response.json();

      if (data.ok) {
        showMessage('success', `Calendar assigned successfully to ${data.assignment.entity_name}`);
        await loadAssignments(); // Reload to show updated assignments
      } else {
        showMessage('error', data.error || 'Failed to assign calendar');
      }
    } catch (error) {
      console.error('Failed to assign calendar:', error);
      showMessage('error', 'Failed to assign calendar');
    } finally {
      setSaving(null);
    }
  };

  const showMessage = (type: 'success' | 'error' | 'info', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleAddGuestEmail = () => {
    const email = guestEmailInput.trim();
    if (!email) {
      showMessage('error', 'Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showMessage('error', 'Please enter a valid email address');
      return;
    }

    if (guestEmails.includes(email)) {
      showMessage('info', 'This email is already in the list');
      return;
    }

    setGuestEmails([...guestEmails, email]);
    setGuestEmailInput('');
  };

  const handleRemoveGuestEmail = (email: string) => {
    setGuestEmails(guestEmails.filter(e => e !== email));
  };

  const handleSaveGuestEmails = async () => {
    if (!selectedEntityId) {
      showMessage('error', 'Please select an entity');
      return;
    }

    setSavingGuestEmails(true);
    try {
      const response = await fetch(`/api/admin/calendar-guest-emails/${selectedEntityId}`, {
        method: 'PUT',
        headers: {
          ...(await orchestratorApi.getAuthHeaders()),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          guest_emails: guestEmails
        })
      });

      const data = await response.json();

      if (data.ok) {
        showMessage('success', `Guest emails updated successfully for ${data.entity.entity_name}`);
        await loadAssignments(); // Reload to show updated data
      } else {
        showMessage('error', data.error || 'Failed to update guest emails');
      }
    } catch (error) {
      console.error('Failed to update guest emails:', error);
      showMessage('error', 'Failed to update guest emails');
    } finally {
      setSavingGuestEmails(false);
    }
  };

  // Get calendars for selected user
  const selectedUserCalendars = connectedUsers.find(
    u => u.user_email === selectedUserEmail
  )?.calendars || [];

  const currentAssignment = assignments.find(a => a.entity_id === selectedEntityId);

  return (
    <div className="gca-card gca-calendar-assignment">
      <h3>🔗 Entity-Calendar Assignments</h3>
      <p className="gca-subtext">
        Assign team members' Google Calendars to specific entities. The AI agent will use the assigned
        calendar for checking availability and booking appointments.
      </p>

      {message && (
        <div className={`gca-message gca-message-${message.type}`}>
          {message.text}
        </div>
      )}

      {loading ? (
        <p className="gca-loading">Loading assignments...</p>
      ) : connectedUsers.length === 0 ? (
        <p className="gca-empty">
          No Google Calendars connected. Please connect a calendar first.
        </p>
      ) : (
        <>
          {/* Entity Selector */}
          <div className="gca-form-section">
            <label htmlFor="entity-select-assignment" className="gca-label">
              Select Entity:
            </label>
            <select
              id="entity-select-assignment"
              className="gca-select"
              value={selectedEntityId}
              onChange={(e) => setSelectedEntityId(e.target.value)}
              disabled={saving !== null}
            >
              <option value="">-- Select Entity --</option>
              {assignments.map((assignment) => (
                <option key={assignment.entity_id} value={assignment.entity_id}>
                  {assignment.entity_name} ({assignment.entity_phone})
                </option>
              ))}
            </select>
          </div>

          {/* Show current assignment */}
          {selectedEntityId && currentAssignment && currentAssignment.assigned_user && (
            <div className="gca-current-assignment">
              <strong>Current Assignment:</strong> {currentAssignment.assigned_user}
              {currentAssignment.assigned_calendars.length > 1 && (
                <span> ({currentAssignment.assigned_calendars.length - 1} calendar{currentAssignment.assigned_calendars.length > 2 ? 's' : ''})</span>
              )}
            </div>
          )}

          {/* Guest Emails Section */}
          {selectedEntityId && (
            <div className="gca-form-section" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '20px', marginTop: '20px' }}>
              <label className="gca-label">
                📧 Calendar Event Guests
              </label>
              <p className="gca-hint" style={{ marginBottom: '12px' }}>
                These email addresses will be automatically added as guests to all calendar events created for this entity.
              </p>

              {/* Guest Email Input */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <input
                  type="email"
                  className="gca-select"
                  style={{ flex: 1 }}
                  placeholder="Enter email address (e.g., tien.ho@eh-immigration.com)"
                  value={guestEmailInput}
                  onChange={(e) => setGuestEmailInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddGuestEmail();
                    }
                  }}
                  disabled={savingGuestEmails}
                />
                <button
                  onClick={handleAddGuestEmail}
                  disabled={savingGuestEmails}
                  className="gca-button gca-button-primary"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  ➕ Add Email
                </button>
              </div>

              {/* Guest Emails List */}
              {guestEmails.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {guestEmails.map((email) => (
                      <div
                        key={email}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          backgroundColor: '#eff6ff',
                          border: '1px solid #bfdbfe',
                          borderRadius: '6px',
                          fontSize: '0.875rem'
                        }}
                      >
                        <span>{email}</span>
                        <button
                          onClick={() => handleRemoveGuestEmail(email)}
                          disabled={savingGuestEmails}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#ef4444',
                            padding: '0 4px',
                            fontSize: '1rem',
                            lineHeight: 1
                          }}
                          title="Remove email"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Save Guest Emails Button */}
              <button
                onClick={handleSaveGuestEmails}
                disabled={savingGuestEmails}
                className="gca-button gca-button-primary"
              >
                {savingGuestEmails ? 'Saving...' : '💾 Save Guest Emails'}
              </button>
            </div>
          )}

          {/* User Selector (Dropdown) */}
          {selectedEntityId && (
            <div className="gca-form-section">
              <label htmlFor="user-select" className="gca-label">
                Calendar Owner:
              </label>
              <select
                id="user-select"
                className="gca-select"
                value={selectedUserEmail}
                onChange={(e) => setSelectedUserEmail(e.target.value)}
                disabled={saving !== null}
              >
                <option value="">-- Select Calendar Owner --</option>
                {connectedUsers.map((user) => {
                  const hasError = user.error || user.last_error || user.is_valid === false;
                  return (
                    <option key={user.user_email} value={user.user_email}>
                      {user.google_email || user.user_email}
                      {hasError ? ' ❌ (Invalid - Please Reconnect)' : ` (${user.calendars.length} calendar${user.calendars.length !== 1 ? 's' : ''})`}
                    </option>
                  );
                })}
              </select>
              {selectedUserEmail && (() => {
                const selectedUser = connectedUsers.find(u => u.user_email === selectedUserEmail);
                const hasError = selectedUser?.error || selectedUser?.last_error || selectedUser?.is_valid === false;
                return hasError && (
                  <p className="gca-error-text" style={{ marginTop: '5px', color: '#ef4444', fontSize: '0.875rem' }}>
                    ⚠️ This user's calendar connection is invalid. Please have them logout and reconnect their Google Calendar.
                    {selectedUser?.last_error && (
                      <span style={{ display: 'block', marginTop: '4px', fontSize: '0.75rem', opacity: 0.8 }}>
                        Error: {selectedUser.last_error}
                      </span>
                    )}
                  </p>
                );
              })()}
            </div>
          )}

          {/* Calendar Selector */}
          {selectedEntityId && selectedUserEmail && selectedUserCalendars.length > 0 && (
            <div className="gca-form-section">
              <label className="gca-label">
                Select Calendar{selectedUserCalendars.length > 1 ? 's' : ''}:
              </label>

              {selectedUserCalendars.length === 1 ? (
                <div className="gca-auto-selected">
                  <div className="gca-calendar-item gca-calendar-item-selected">
                    <div className="gca-calendar-info">
                      <div className="gca-calendar-name">
                        ✓ {selectedUserCalendars[0].summary}
                        {selectedUserCalendars[0].isPrimary && (
                          <span className="gca-badge gca-badge-primary">Primary</span>
                        )}
                      </div>
                      {selectedUserCalendars[0].description && (
                        <div className="gca-calendar-description">{selectedUserCalendars[0].description}</div>
                      )}
                      <div className="gca-calendar-id">{selectedUserCalendars[0].id}</div>
                    </div>
                    <div
                      className="gca-calendar-color"
                      style={{ backgroundColor: selectedUserCalendars[0].backgroundColor }}
                    />
                  </div>
                  <p className="gca-hint">Only one calendar available - automatically selected</p>
                </div>
              ) : (
                <div className="gca-calendars-list">
                  {selectedUserCalendars.map((calendar) => {
                    const isSelected = selectedCalendarIds.includes(calendar.id);
                    return (
                      <div
                        key={calendar.id}
                        className={`gca-calendar-item ${isSelected ? 'gca-calendar-item-selected' : ''}`}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedCalendarIds(selectedCalendarIds.filter(id => id !== calendar.id));
                          } else {
                            setSelectedCalendarIds([...selectedCalendarIds, calendar.id]);
                          }
                        }}
                      >
                        <div className="gca-calendar-checkbox">
                          <input
                            type="checkbox"
                            id={`assignment-calendar-${calendar.id}`}
                            checked={isSelected}
                            onChange={() => {
                              if (isSelected) {
                                setSelectedCalendarIds(selectedCalendarIds.filter(id => id !== calendar.id));
                              } else {
                                setSelectedCalendarIds([...selectedCalendarIds, calendar.id]);
                              }
                            }}
                          />
                          <label htmlFor={`assignment-calendar-${calendar.id}`}>
                            <div className="gca-calendar-info">
                              <div className="gca-calendar-name">
                                {calendar.summary}
                                {calendar.isPrimary && (
                                  <span className="gca-badge gca-badge-primary">Primary</span>
                                )}
                              </div>
                              {calendar.description && (
                                <div className="gca-calendar-description">{calendar.description}</div>
                              )}
                              <div className="gca-calendar-id">{calendar.id}</div>
                            </div>
                          </label>
                        </div>
                        <div
                          className="gca-calendar-color"
                          style={{ backgroundColor: calendar.backgroundColor }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Save Button */}
          {selectedEntityId && selectedUserEmail && selectedCalendarIds.length > 0 && (
            <div className="gca-save-section">
              <button
                onClick={handleSaveAssignment}
                disabled={saving !== null}
                className="gca-button gca-button-primary"
              >
                {saving === selectedEntityId
                  ? 'Saving...'
                  : `💾 Save Assignment (${selectedCalendarIds.length} calendar${selectedCalendarIds.length !== 1 ? 's' : ''})`}
              </button>
              {selectedCalendarIds.length > 0 && (
                <span className="gca-hint">
                  First calendar: {selectedUserCalendars.find(c => c.id === selectedCalendarIds[0])?.summary || 'Unknown'}
                </span>
              )}
            </div>
          )}

          {/* Assignment Summary Table */}
          <div className="gca-assignments-summary">
            <h4>Current Assignments</h4>
            <table className="gca-table">
              <thead>
                <tr>
                  <th>Entity</th>
                  <th>Assigned Calendar Owner</th>
                  <th>Calendars</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((assignment) => {
                  const calendarCount = assignment.assigned_calendars.length > 0
                    ? assignment.assigned_calendars.length - 1
                    : 0;

                  return (
                    <tr key={assignment.entity_id}>
                      <td>
                        <strong>{assignment.entity_name}</strong>
                        <br />
                        <span className="gca-text-muted">{assignment.entity_phone}</span>
                      </td>
                      <td>
                        {assignment.assigned_user || (
                          <span className="gca-text-muted">Not assigned</span>
                        )}
                      </td>
                      <td>
                        {calendarCount > 0 ? (
                          <span>{calendarCount} selected</span>
                        ) : (
                          <span className="gca-text-muted">None</span>
                        )}
                      </td>
                      <td>
                        {assignment.assigned_user ? (
                          <span className="gca-badge gca-badge-active">Configured</span>
                        ) : (
                          <span className="gca-badge gca-badge-inactive">Not Configured</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Info Box */}
          <div className="gca-note" style={{ marginTop: '20px' }}>
            <strong>ℹ️ How It Works:</strong>
            <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
              <li>Select an entity (WhatsApp Business account)</li>
              <li>Choose which team member's calendar to use</li>
              <li>Select specific calendars from that user's Google account</li>
              <li>The AI agent will check these calendars before booking appointments</li>
              <li>First selected calendar is used as the default booking destination</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default CalendarAssignment;
